import React, { useCallback, useEffect, useState } from 'react';
import { css } from "@emotion/react";
import { isEmpty } from "lodash";
import jwt_decode from "jwt-decode";

import useForm, { FieldList } from 'core/hooks/useForm';
import { login, socialLogin, SocialLoginType } from 'core/includes/auth';
import theme from 'theme/index';
import Loading from "core/Components/Utils/Loading";
import LabelledField from "core/Components/Form/LabelledField";
import Input from "core/Components/Form/Input";
import FormActions from "core/Components/Form/FormActions";
import SocialLogin from "pages/Components/auth/components/SocialLogin";
import RoundButton from "core/Components/Buttons/RoundButton";
import { useAppState } from "core/contexts/AppContext";
import PasswordRecovery from "pages/Components/auth/components/PasswordRecovery";
import Dialog from "core/Components/Dialog/Dialog";
import Form from "core/Components/Form/Form";
import { NOOP } from "core/constants";
import LoginError from "pages/includes/LoginError";
import useUser from "core/hooks/useUser";

type Props = {
    errorMessage?: string,
    onSignUp?: () => void,
    onLoginDialogClose?: () => void,
};

const validator = (fields: FieldList) => {
    const errors: any = {};

    if (!fields.email) {
        errors.email = 'Please provide a email';
    }

    if (!fields.password) {
        errors.password = 'Please provide a password';
    }

    return errors;
};

const SignIn: React.FC<Props> = (
    {
        errorMessage,
        onSignUp,
        onLoginDialogClose = NOOP,
    }
) => {
    const [loginError, setLoginError] = useState({ isVisible: false, errorMessage: '', isHtml: false });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [showForgotPassword, setShowForgotPassword] = useState(false);
    const [appState] = useAppState();

    const { getAndSaveUser } = useUser();

    const { fields, errors, onFieldChange, validate } = useForm({
        fields: { email: '', password: '' },
        validator,
    });

    useEffect(() => {
        if (!isEmpty(errorMessage)) {
            setLoginError({ isVisible: true, errorMessage: errorMessage || '', isHtml: false });
        }
    }, [errorMessage]);

    const submitSignInForm = () => {
        setLoginError({ isVisible: false, errorMessage: '', isHtml: false });
        if (validate()) {
            setIsSubmitting(true);
            login(fields.email, fields.password).then((data: any) => {
                if (data.status) {
                    getAndSaveUser()
                }
            }).catch((e: any) => {
                setIsSubmitting(false);
                setLoginError({ isVisible: true, errorMessage: e.message, isHtml: true });
            })
        }
    }

    const socialSignIn = useCallback((socialProfile: any, socialLoginType: SocialLoginType) => {
        setLoginError({ isVisible: false, errorMessage: '', isHtml: false });

        let email: string = '';
        const credential = socialProfile["credential"];
        if (socialLoginType === "google") {
            const decodedData: any = jwt_decode(credential);
            email = decodedData.email;
        } else {
            email = socialProfile.email;
        }

        socialLogin(
            email,
            credential,
            socialLoginType
        )
            .then(getAndSaveUser)
            .catch((e: LoginError) => {
            setLoginError({ isVisible: true, errorMessage: e.message, isHtml: false });
        });
    }, [getAndSaveUser]);

    const clickForgotPassword = () => {
        setShowForgotPassword(true);
    }

    const onClickClose = (e: any) => {
        e.preventDefault();
        onLoginDialogClose();
        appState.clearUser();
    }

    return (
        <div css={authContent}>
            {isSubmitting && <Loading onTop overlay overlayColour={"white"} />}
            <div css={leftHeader}>Log in</div>
            {loginError.isVisible && (
                <div css={loginErrorSection}>
                    {loginError.isHtml ? (
                        <div dangerouslySetInnerHTML={{ __html: loginError.errorMessage }} />
                    ) : (
                        <span>{loginError.errorMessage}</span>
                    )}
                </div>
            )}
            <div css={formContainerStyle}>
                <Form onSubmit={submitSignInForm}>
                    <LabelledField
                        name="email"
                        error={errors.email}
                        label="Email / Username"
                        version={'normal'}
                    >
                        <Input
                            hasError={!!errors.email}
                            css={emailInput}
                            label="Enter email address or username"
                            name="email"
                            onChange={onFieldChange}
                            value={fields.email}
                            hasBorder
                        />
                    </LabelledField>
                    <LabelledField
                        name="password"
                        error={errors.password}
                        label="Password"
                        version={'normal'}
                    >
                        <Input
                            hasError={!!errors.password}
                            css={passwordInput}
                            label="Password"
                            type="password"
                            name="password"
                            onChange={onFieldChange}
                            value={fields.password}
                            hasBorder
                        />
                    </LabelledField>
                    <div css={formActions}>
                        <FormActions customCss={customFormActionsStyles}>
                            <RoundButton
                                css={formSubmitButton}
                                type="submit"
                                forceLoading={isSubmitting}
                            >
                                Log in
                            </RoundButton>
                            {onLoginDialogClose !== NOOP && (
                                <RoundButton
                                    onButtonClick={onClickClose}
                                    forceLoading={isSubmitting}
                                    css={formSubmitButtonLoginDialogStyle}
                                >
                                    Close
                                </RoundButton>
                            )}
                        </FormActions>
                    </div>
                    <div css={lineContainerStyle}>
                        <div css={lineStyle} />
                        <span css={lineTextStyle}>or</span>
                        <div css={lineStyle} />
                    </div>
                    <SocialLogin socialSignUp={socialSignIn} />
                    <div css={signInTextContainer}>
                        <div css={signInText}>
                            Don&apos;t have an account? <span css={signUpStyle} onClick={onSignUp}>Create one</span>
                        </div>
                        <div css={signInText}>
                            Forgot your password?
                            Please <div onClick={clickForgotPassword} css={signUpStyle}>click here</div>.
                        </div>
                    </div>
                </Form>
            </div>
            {showForgotPassword && (
                <Dialog onClose={() => setShowForgotPassword(false)}>
                    <PasswordRecovery onSuccess={() => setShowForgotPassword(false)} />
                </Dialog>
            )}
        </div>
    );
};

export default SignIn;


const lineContainerStyle = css`
    display: flex;

    ${theme.breakpoints.up("5xl")} {
        margin: 68px 0;
    }

    ${theme.breakpoints.between("xxl", "4xl")} {
        margin-bottom: 38px;
    }

    ${theme.breakpoints.between("lg", "xl")} {
        margin-bottom: 28px;
    }

    ${theme.breakpoints.only('md')} {
        margin-bottom: 21px;
    }

    ${theme.breakpoints.only("sm")} {
        margin-bottom: 25px;
    }

    ${theme.breakpoints.down("xs")} {
        margin-bottom: 18px;
    }
`;

const lineTextStyle = css`
    padding: 0 28px;
    margin-top: -8px;
    color: ${theme.colours.silverChalice2};

    ${theme.breakpoints.up("5xl")} {
        font-size: 18px;
        margin-top: -10px;
    }

    ${theme.breakpoints.between("xxl", "4xl")} {
        font-size: 14px;
    }

    ${theme.breakpoints.down("xl")} {
        font-size: 11px;
        margin-top: -6px;
    }
`;

const lineStyle = css`
    height: 1px;
    flex-grow: 1;
    background: ${theme.colours.silverChalice2};
`;

const authContent = css`
    margin-top: 40px;
    width: 510px;
    position: relative;

    ${theme.breakpoints.down('sm')} {
        width: 100%;
        max-width: 510px;
        margin: 40px auto 0;
    }

    ${theme.breakpoints.down('xs')} {
        padding: 0 20px;
    }
`;

const formContainerStyle = css`
    ${theme.breakpoints.up("5xl")} {
        margin-top: 18px;
    }

    ${theme.breakpoints.between("xxl", "4xl")} {
        margin-top: 21px;
    }

    ${theme.breakpoints.between("lg", "xl")} {
        margin-top: 13px;
    }

    ${theme.breakpoints.only("md")} {
        margin-top: 16px;
    }

    ${theme.breakpoints.only("sm")} {
        margin-top: 8px;
    }

    ${theme.breakpoints.down("xs")} {
        margin-top: 6px;
    }
`;

const loginErrorSection = css`
    width: 100%;
    border-radius: 3px;
    border: 1px solid ${theme.colours.crimson};
    color: ${theme.colours.red[999]};
    margin-top: 6px;
    margin-bottom: 20px;

    span {
        margin: 10px;
    }
`;

const emailInput = css`
    margin-top: 0;
    box-sizing: border-box;
    width: 100%;
`;

const passwordInput = css`
    margin-top: 0;
    box-sizing: border-box;
    width: 100%;
`;

const signInTextContainer = css`
    ${theme.breakpoints.up("3xl")} {
        margin-top: 67px;

        > div {
            margin-bottom: 22px;
        }
    }

    ${theme.breakpoints.between("lg", "xxl")} {
        margin-top: 53px;
    }

    ${theme.breakpoints.between("sm", "xxl")} {
        > div {
            margin-bottom: 17px;
        }
    }

    ${theme.breakpoints.only("md")} {
        margin-top: 32px;
    }

    ${theme.breakpoints.only("sm")} {
        margin-top: 53px;

    }

    ${theme.breakpoints.down("xs")} {
        margin-top: 46px;

        > div {
            margin-bottom: 28px;
        }
    }
`;

const signInText = css`
    font-size: 14px;
    text-align: center;

    a {
        text-decoration: none;
        color: ${theme.colours.blue['400']}
    }

    a:hover {
        text-decoration: underline;
    }

    ${theme.breakpoints.down('sm')} {
        width: 100%;
    }
`;

const formActions = css`
    display: flex;
    flex-direction: row;
    justify-content: flex-end;

    ${theme.breakpoints.up('sm')} {
        margin-top: 20px;
        justify-content: center;
    }

    ${theme.breakpoints.up("5xl")} {
        margin: 63px 0 68px;
    }

    ${theme.breakpoints.between("xxl", "4xl")} {
        margin: 41px 0 45px;
    }

    ${theme.breakpoints.between("lg", "xl")} {
        margin: 29px 0 34px;
    }

    ${theme.breakpoints.only("md")} {
        margin: 21px 0 26px;
    }

    ${theme.breakpoints.only("sm")} {
        margin: 26px 0 31px;
    }

    ${theme.breakpoints.down("xs")} {
        margin: 19px 0 24px;
    }
`;

const customFormActionsStyles = css`
    width: 100%;
    justify-content: center;
`;

const formSubmitButton = css`
    background-color: ${theme.colours.blue['400']};
    width: 400px;
    border-radius: 45px;
    height: 40px;
    padding: 0;

    ${theme.breakpoints.down('xs')} {
        width: 250px;
    }
`;

const leftHeader = css`
    color: ${theme.colours.curiousBlue};
    font-weight: ${theme.fonts.weights.bold};

    ${theme.breakpoints.up("xxl")} {
        font-size: 50px;
        line-height: 75px;
    }

    ${theme.breakpoints.between("md", "xl")} {
        font-size: 43px;
        line-height: 65px;
    }

    ${theme.breakpoints.down("sm")} {
        font-size: 30px;
        line-height: 45px;
    }
`;

const signUpStyle = css`
    display: inline-block;
    color: ${theme.colours.curiousBlue};
    cursor: pointer;
    text-decoration: none;

    &:hover {
        text-decoration: underline;
    }
`;

const formSubmitButtonLoginDialogStyle = css`
    width: 49%;
    height: 42px;
    font-size: 20px;

    ${theme.breakpoints.down('xs')} {
        width: 100%;
    }
`;